<template>
  <Card class="w-full max-w-md mx-auto">
    <CardHeader>
      <CardTitle>
        {{ $t(`auth.${setPasswordType}.set-password.title`) }}
      </CardTitle>
    </CardHeader>

    <CardContent>
      <div class="space-y-6">
        <Field>
          <Label for="new-password">
            {{ $t('user-profile.fields.new-password.title') }}
          </Label>
          <div class="relative">
            <Input
                id="new-password"
                name="new-password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="********"
                v-model="newPassword"
                @focus="newPasswordFocused = true"
                @blur="newPasswordFocused = false"
                :class="{ 'border-red-500': v$.newPassword.$error }"
            ></Input>
            <button
                type="button"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
                @click="togglePasswordVisibility('new')"
                tabindex="-1"
            >
              <CloseEyeIcon v-if="showPassword" />
              <OpenEyeIcon v-else />
            </button>
          </div>

          <FieldError v-if="v$.newPassword.$error" :errors="v$.newPassword.$errors" />

          <div
              v-if="newPasswordFocused || newPassword.length > 0"
              class="mt-3 flex flex-col gap-2 text-sm"
          >
            <div
                v-for="rule in passwordRules"
                :key="rule.key"
                class="flex items-center gap-2"
                :class="{
                      'text-green-600 dark:text-green-400': rule.isValid,
                      'text-gray-500 dark:text-gray-400': !rule.isValid
                  }"
            >
              <svg
                  v-if="rule.isValid"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="w-4 h-4"
              >
                <path fill-rule="evenodd" d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-7.425 10.158a.75.75 0 0 1-1.154.114l-4.575-3.86a.75.75 0 0 1 .917-1.29l3.966 3.35L19.31 4.786a.75.75 0 0 1 1.04-.208Z" clip-rule="evenodd" />
              </svg>
              <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="w-4 h-4"
              >
                <path fill-rule="evenodd" d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd" />
              </svg>

              <span>{{ $t(rule.text) }}</span>
            </div>
          </div>
        </Field>

        <Field>
          <Label for="confirm-password">
            {{ $t('user-profile.fields.confirm-password.title') }}
          </Label>
          <div class="relative">
            <Input
                id="confirm-password"
                name="confirm-password"
                :type="showConfirmPassword ? 'text' : 'password'"
                placeholder="********"
                v-model="confirmPassword"
                :class="{ 'border-red-500': v$.confirmPassword.$error }"
            ></Input>
            <button
                type="button"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
                @click="togglePasswordVisibility('confirm')"
                tabindex="-1"
            >
              <CloseEyeIcon v-if="showConfirmPassword" />
              <OpenEyeIcon v-else />
            </button>
          </div>
          <FieldError v-if="v$.confirmPassword.$error" :errors="v$.confirmPassword.$errors" />
        </Field>
      </div>
    </CardContent>

    <CardFooter>
      <Button
          class="w-full bg-[#64748B] transition-colors hover:bg-[#64748B] hover:opacity-75"
          :disabled="isProfileSaving"
          @click="setNewPassword"
      >{{ $t(`auth.${setPasswordType}.set-password.actions.save`) }}</Button>
    </CardFooter>
  </Card>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import useVuelidate from '@vuelidate/core'
import { minLength, required, sameAs } from '@vuelidate/validators'
import {
  existDigit,
  existLowerCase,
  existUpperCase,
} from '@js-app/services/validators' // Ваші валідатори

import { UserApi } from '@js-app/office/api/user-api'

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from '@/components/ui/card'
import { Field, FieldError } from '@/components/ui/field'
import { Label } from '@/components/ui/label'
import {Input} from "@/components/ui/input"
import { Button } from "@/components/ui/button"

import OpenEyeIcon from "@/vue/components/icons/ OpenEyeIcon.vue";
import CloseEyeIcon from "@/vue/components/icons/CloseEyeIcon.vue";
import {toast} from "vue-sonner";
import { getCurrentInstance } from 'vue';

const route = useRoute()
const router = useRouter()
const instance = getCurrentInstance()
const t = (key: string) => instance?.ctx.$i18n.t(key)

// --- State ---
const isProfileSaving = ref(false)
const newPasswordFocused = ref(false)
const newPassword = ref('')
const confirmPassword = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const error = ref<string | null>(null)

const checkMinLength = (val: string) => val.length >= 8;
const checkHasDigit = (val: string) => /\d/.test(val);
const checkHasLowerCase = (val: string) => /[a-z]/.test(val);
const checkHasUpperCase = (val: string) => /[A-Z]/.test(val);
const checkOnlyAllowedChars = (val: string) => /^[a-zA-Z0-9]+$/.test(val);


// --- Computed ---
const setPasswordType = computed(() => {
  return route.meta.setPasswordType || 'forgot-password'
})

const passwordRules = computed(() => {
  const password = newPassword.value;

  if (password.length === 0 && !newPasswordFocused.value) {
    return [
      { key: 'min-8', text: 'user-profile.hints.change-password.min-8', isValid: false },
      { key: 'one-lower', text: 'user-profile.hints.change-password.one-lower', isValid: false },
      { key: 'one-upper', text: 'user-profile.hints.change-password.one-upper', isValid: false },
      { key: '0-9', text: 'user-profile.hints.change-password.0-9', isValid: false },
      { key: 'allowed-chars', text: 'user-profile.hints.change-password.allowed-chars-only', isValid: false }
    ];
  }

  return [
    {
      key: 'min-8',
      text: 'user-profile.hints.change-password.min-8',
      isValid: checkMinLength(password),
    },
    {
      key: 'one-lower',
      text: 'user-profile.hints.change-password.one-lower',
      isValid: checkHasLowerCase(password),
    },
    {
      key: 'one-upper',
      text: 'user-profile.hints.change-password.one-upper',
      isValid: checkHasUpperCase(password),
    },
    {
      key: '0-9',
      text: 'user-profile.hints.change-password.0-9',
      isValid: checkHasDigit(password),
    },
    { key: 'allowed-chars',
      text: 'user-profile.hints.change-password.allowed-chars-only',
      isValid: checkOnlyAllowedChars(password)
    }
  ];
});


const rules = computed(() => ({
  newPassword: {
    required,
    existDigit,
    existLowerCase,
    existUpperCase,
    minLength: minLength(8),
  },

  confirmPassword: {
    required,
    sameAsPassword: sameAs(newPassword),
  },
}))

const v$ = useVuelidate(rules, { newPassword, confirmPassword })


// --- Methods ---
function togglePasswordVisibility(field: 'new' | 'confirm') {
  if (field === 'new') {
    showPassword.value = !showPassword.value
  } else {
    showConfirmPassword.value = !showConfirmPassword.value
  }
}

const setNewPassword = async () => {
  if (newPassword.value !== confirmPassword.value) {
    toast.error(t('cabinet-forgot-password.errors.validation_repeat_password'), {
      description: t('cabinet-forgot-password.errors.validation_repeat_password_text')
    })

    await v$.value.confirmPassword.$validate();
    return
  }

  const isValid = await v$.value.$validate()

  if (!isValid) {
    toast.error(t('cabinet-forgot-password.errors.validation_password'), {
      description: t('cabinet-forgot-password.errors.validation_password_text')
    });
    return
  }

  isProfileSaving.value = true

  try {
    const response = await UserApi.setNewPassword(newPassword.value)
    if (response.data.success) {
      toast.success(t('cabinet-forgot-password.toast.password.success'), {
        description: t('cabinet-forgot-password.toast.password.success_text')
      });

      setTimeout(() => {
        router.push({ name: 'login' })
      }, 1500);
    } else {
      toast.error(t('cabinet-forgot-password.toast.password.error_save'), {
        description: t('cabinet-forgot-password.toast.password.error_save_text'),
      });
    }

  } catch (e: any) {
    console.error('Password set failed:', e);

    let errorMessage = 'Виникла невідома помилка. Спробуйте ще раз.';

    if (e && e.data && e.data.errors && e.data.errors.Message) {
      // Тут може бути обробка специфічних помилок від бекенду
      errorMessage = e.data.errors.Message;
    } else if (e && e.message) {
      errorMessage = `Помилка: ${e.message}`;
    }

    toast.error(t('cabinet-forgot-password.toast.password.error_change'), {
      description: errorMessage
    })

  } finally {
    isProfileSaving.value = false
  }
}
</script>

<style scoped>
/* Стилі Tailwind CSS */
.mx-auto {
  margin-left: auto;
  margin-right: auto;
}
</style>
